import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

import en from '../locales/en.json';
import fr from '../locales/fr.json';
import de from '../locales/de.json';
import it from '../locales/it.json';
import es from '../locales/es.json';
import nl from '../locales/nl.json';
import pl from '../locales/pl.json';
import pt from '../locales/pt.json';
import zh from '../locales/zh.json';
import tr from '../locales/tr.json';
import cs from '../locales/cs.json';
import sv from '../locales/sv.json';
import ru from '../locales/ru.json';
import ja from '../locales/ja.json';
import ar from '../locales/ar.json';

const translations = { en, fr, de, it, es, nl, pl, pt, zh, tr, cs, sv, ru, ja, ar };
const SUPPORTED_LANGS = Object.keys(translations);

export const LANGUAGE_OPTIONS = [
  { code: 'fr', label: 'Français',   flag: '🇫🇷' },
  { code: 'en', label: 'English',    flag: '🇬🇧' },
  { code: 'de', label: 'Deutsch',    flag: '🇩🇪' },
  { code: 'it', label: 'Italiano',   flag: '🇮🇹' },
  { code: 'es', label: 'Español',    flag: '🇪🇸' },
  { code: 'nl', label: 'Nederlands', flag: '🇳🇱' },
  { code: 'pl', label: 'Polski',     flag: '🇵🇱' },
  { code: 'pt', label: 'Português',  flag: '🇵🇹' },
  { code: 'zh', label: '中文',        flag: '🇨🇳' },
  { code: 'tr', label: 'Türkçe',     flag: '🇹🇷' },
  { code: 'cs', label: 'Čeština',    flag: '🇨🇿' },
  { code: 'sv', label: 'Svenska',    flag: '🇸🇪' },
  { code: 'ru', label: 'Русский',    flag: '🇷🇺' },
  { code: 'ja', label: '日本語',      flag: '🇯🇵' },
  { code: 'ar', label: 'العربية',    flag: '🇸🇦' },
];

const LanguageContext = createContext(null);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

const detectInitialLanguage = () => {
  if (typeof window === 'undefined') return 'fr';
  const saved = localStorage.getItem('language');
  if (saved && SUPPORTED_LANGS.includes(saved)) return saved;
  // Auto-detect from browser
  const navLangs = (navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language]) || [];
  for (const l of navLangs) {
    const code = (l || '').slice(0, 2).toLowerCase();
    if (SUPPORTED_LANGS.includes(code)) return code;
  }
  return 'fr'; // default to French (primary market)
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(detectInitialLanguage);

  useEffect(() => {
    localStorage.setItem('language', language);
    document.documentElement.lang = language;
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
    const token = localStorage.getItem('token');
    if (token) {
      axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/me/language`,
        { language },
        { headers: { Authorization: `Bearer ${token}` } }
      ).catch(() => {});
    }
  }, [language]);

  const t = (key) => {
    return translations[language]?.[key] ?? translations.en[key] ?? key;
  };

  const getLocalizedName = (item) => {
    if (!item) return '';
    const key = `name_${language}`;
    return item[key] || item.name || '';
  };

  const getLocalizedDescription = (item) => {
    if (!item) return '';
    const key = `description_${language}`;
    return item[key] || item.description || '';
  };

  const value = {
    language,
    setLanguage,
    t,
    getLocalizedName,
    getLocalizedDescription,
    supportedLanguages: LANGUAGE_OPTIONS,
    isEnglish: language === 'en',
    isFrench: language === 'fr',
  };

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};
